"use client"

import { useQuery } from "@tanstack/react-query"
import { useDebounce } from "@uidotdev/usehooks"
import { useParams, useRouter } from "next/navigation"
import { useCallback, useEffect, useRef, useState } from "react"

import { SearchItemsParams } from "@lib/clients/nelo-api-client"
import { neloApiClient } from "@lib/config-client"
import { useScrollLock } from "@lib/hooks/use-scroll-lock"
import { buildRoute } from "@lib/routes"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

import { BinocularsIcon } from "../search-bar/BinocularsIcon"
import SearchResults from "../SearchResults"

interface MobileSearchProps {
  dismissSearchInput: () => void
}

export const MobileSearch = ({ dismissSearchInput }: MobileSearchProps) => {
  const { countryCode } = useParams<{ countryCode: string; categorias: string[] }>()
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [showResults, setShowResults] = useState<boolean>(false)
  const debouncedSearchTerm = useDebounce(searchTerm, 500)
  const inputRef = useRef<HTMLInputElement>(null)
  const router = useRouter()
  const { lockScroll, unlockScroll } = useScrollLock()

  const { data: searchResults } = useQuery({
    queryKey: ["search", debouncedSearchTerm],
    queryFn: async ({ queryKey: [_, searchTerm] }) => {
      const params = {
        searchString: searchTerm,
        searchTypes: ["MARKETPLACE"],
      } as SearchItemsParams
      const response = await neloApiClient.searchItems(params)
      return response.result
    },
    enabled: debouncedSearchTerm.length >= 1,
  })

  // When search results are shown, disable scrolling
  useEffect(() => {
    if (searchResults && searchResults.length > 0) {
      setShowResults(true)
      lockScroll()
    } else {
      setShowResults(false)
      unlockScroll()
    }

    // Cleanup function always restores original scroll state
    return unlockScroll
  }, [searchResults, lockScroll, unlockScroll])

  const handleClearSearch = () => {
    setSearchTerm("")
    setShowResults(false)
    unlockScroll()
    dismissSearchInput()
  }

  const onSearch = useCallback(
    (searchTerm: string) => {
      if (searchTerm.length === 0) {
        return
      }

      router.push(`/${countryCode}${buildRoute.search(searchTerm)}`)
    },
    [countryCode, router]
  )

  const handleResultPress = (url: string) => {
    setShowResults(false)
    unlockScroll()
    router.push(url)
    dismissSearchInput()
  }

  return (
    <div className="relative w-full">
      <div className="flex w-full items-center rounded-full bg-nelo-light-gray hover:bg-[#F0F9FF]">
        <input
          className="relative z-10 w-full rounded-full border-2 border-transparent bg-transparent py-2 pl-4 pr-16 text-[14px] outline-none transition-colors duration-100 focus:border-nelo-blue focus:bg-white"
          placeholder="Busca electrónicos, moda y más"
          onChange={(e) => setSearchTerm(e.target.value)}
          value={searchTerm}
          ref={inputRef}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault()
              onSearch(searchTerm)
            }
          }}
        />
        <button
          onClick={() => onSearch(searchTerm)}
          className="absolute right-0 z-20 flex h-[30px] min-w-[30px] items-center justify-center rounded-full bg-nelo-blue"
        >
          <BinocularsIcon className="text-[12px]" />
        </button>
      </div>

      {showResults && searchResults && searchResults.length > 0 && (
        <>
          <div className="fixed inset-x-0 bottom-0 top-[130px] bg-black bg-opacity-30" onClick={handleClearSearch} aria-hidden="true" />
          <div className="absolute left-0 top-[calc(100%+5px)] z-dropdown w-full">
            <div className="mx-auto w-full max-w-[500px]">
              <SearchResults results={searchResults} onPress={handleResultPress}>
                <LocalizedClientLink
                  href={buildRoute.search(searchTerm)}
                  className="block w-full px-4 py-2 text-sm leading-[15px] text-nelo-blue"
                  onClick={handleClearSearch}
                >
                  Ver todos los resultados
                </LocalizedClientLink>
              </SearchResults>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
